import { Component, Input, OnInit } from '@angular/core';
import { DynamicField, DynamicValuesType, PaymentDetailType } from '@cargos/sprintpay-models';
import { filter, map, switchMap, take } from 'rxjs';
import Swal from 'sweetalert2';
import { Detail } from '../../models/details';
import { CustomerService, DynamicsFieldsService } from '../../services';
import { DetailsFieldComponent } from '../details-section/details-field/details-field.component';
import { DetailsHeaderComponent } from '../details-section/details-header/details-header.component';
import { FilesContainerComponent } from '../files/files-container';

@Component({
    selector: 'app-company-customer-reference',
    standalone: true,
    imports: [DetailsFieldComponent, DetailsHeaderComponent, FilesContainerComponent],
    templateUrl: './company-customer-reference.component.html',
    styleUrl: './company-customer-reference.component.scss',
})
export class CompanyCustomerReferenceComponent implements OnInit {
    public details: Detail[] = [];
    public dynamicFields: Detail[] | null = [];
    public companyName: string = '';
    public files: string[] = [];
    public fileName: string = '';

    @Input() paymentDetails: PaymentDetailType;

    constructor(
        private customerService: CustomerService,
        private dynamicsFieldsService: DynamicsFieldsService
    ) {}

    ngOnInit(): void {
        this.loadCompanyCustomerReferenceFields();
        this.loadDynamicFields();
    }

    private loadCompanyCustomerReferenceFields(): void {
        this.companyName = this.customerService.getCompanyName() ?? '';
        const getCompanyCustomerReference = this.customerService.getCompanyCustomerReferenceFieldsBuilder();
        this.details = getCompanyCustomerReference(this.paymentDetails);
    }

    private loadDynamicFields(): void {
        this.dynamicsFieldsService
            .getLoadingDynamicFields()
            .pipe(
                filter((loading) => !loading),
                take(1),
                switchMap(() =>
                    this.dynamicsFieldsService.getDynamicFieldsBySection$(0).pipe(
                        take(1),
                        map((dynamicFields) => this.loadAttachments(this.paymentDetails, dynamicFields)),
                        map((dynamicFields) =>
                            this.getPaymentDynamicFieldsAndValues(this.paymentDetails, dynamicFields)
                        )
                    )
                )
            )
            .subscribe({
                next: (formattedDynamicFields) => {
                    this.dynamicFields = formattedDynamicFields;
                },
                error: (error) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: 'error',
                    });
                },
            });
    }

    private getPaymentDynamicFieldsAndValues(
        payment: PaymentDetailType,
        dynamicFields: DynamicField[] | null
    ): Detail[] {
        if (!dynamicFields || !dynamicFields?.length) {
            return [];
        }

        const dynamicValues = payment?.dynamicValues as DynamicValuesType[];

        const fields = dynamicFields.map((field) => {
            const value = dynamicValues.find((value) => field.id === value.dynamicField?.id);

            return {
                label: field.label,
                value: value?.value ?? 'N/A',
                show: true,
            };
        });

        return fields;
    }

    private loadAttachments(payment: PaymentDetailType, dynamicFields: DynamicField[] | null): DynamicField[] | null {
        const dynamicFieldsAttachments = dynamicFields?.filter((field) => field.fieldType === 'file');

        if (dynamicFieldsAttachments?.length) {
            const dynamicValues = payment?.dynamicValues as DynamicValuesType[];

            this.fileName = dynamicFieldsAttachments?.length ? (dynamicFieldsAttachments[0].label ?? '') : '';

            dynamicFieldsAttachments.forEach((field) => {
                const value = dynamicValues.find((value) => field.id === value.dynamicField?.id);

                if (value?.value) {
                    this.files.push(value.value);
                }
            });

            const filteredDynamicFieldsAttachments =
                dynamicFields?.filter((field) => field.fieldType !== 'file') ?? null;

            return filteredDynamicFieldsAttachments;
        }

        return dynamicFields;
    }
}
